import './EngagementBuyersVisibilityTable.scss';
import React, { useEffect, useState } from 'react';
import BootstrapTable, {
    TableColumnFilterProps,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import filterFactory, {
    textFilter,
} from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TableFilter } from '../../../../components/Table/models/table-filter';
import { useStateSelector } from '../../../../store/selectors';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import { EngagementBuyerVisibilityState } from '../../../../slices/marketplace/timeline/models/engagement-buyer-visibility-state.model';
import BuyerProfilePreviewPopup from '../../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import ClickableText from '../BuyersSelectionStage/ColumnTemplates/ColumnElements/ClickableText';
import { MarketplaceBuyer } from '../../../../slices/marketplace/buyer/models/buyer';
import { Button } from 'reactstrap';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';

interface BuyerWithVisibility extends MarketplaceBuyer {
    isBuyerVisible: boolean;
    isTeaserPosted: boolean;
    isSelectedForTeaser?: boolean;
}

interface EngagementBuyersVisibilityTableProps {
    engagementId: number;
    isLocked: boolean;
    buyers: MarketplaceBuyer[];
    buyersVisibilityStates: EngagementBuyerVisibilityState[];
    refreshBuyersData: () => void;
    isTeaserPostingAvailable: boolean;
    wasTeaserPostingCompleted: boolean;
}

const EngagementBuyersVisibilityTable = (
    props: EngagementBuyersVisibilityTableProps
) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [buyers, setBuyers] = useState<BuyerWithVisibility[]>([]);

    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [selectedBuyerId, setSelectedBuyerId] = useState<number>(0);

    const [
        selectedBuyerIdToToggleVisibility,
        setSelectedBuyerIdToToggleVisibility,
    ] = useState<number>(0);
    const [isBuyerVisibilityLoading, setIsBuyerVisibilityLoading] =
        useState(false);
    const [
        isVisibilityConfirmationPopupVisible,
        setIsVisibilityConfirmationPopupVisible,
    ] = useState(false);

    const [buyerIdsToPostTeaser, setBuyerIdsToPostTeaser] = useState<number[]>(
        []
    );
    const [isTeaserPostingLoading, setIsTeaserPostingLoading] = useState(false);
    const [
        isTeaserPostConfirmationPopupVisible,
        setIsTeaserPostConfirmationPopupVisible,
    ] = useState(false);

    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5000);

    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'companyName',
        })
    );

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'companyName',
            order: 'asc',
        },
    ];

    const mapBuyers = (
        buyers?: MarketplaceBuyer[],
        visibilityStates?: EngagementBuyerVisibilityState[]
    ) => {
        const buyersWithVisibility: BuyerWithVisibility[] = (
            buyers ?? props.buyers
        ).map((buyer) => {
            const visibilityState = (
                visibilityStates ?? props.buyersVisibilityStates
            ).find((state) => state.buyerId === buyer.id);
            return {
                ...buyer,
                isBuyerVisible: !!visibilityState,
                isTeaserPosted: visibilityState?.isTeaserPosted ?? false,
            };
        });

        return buyersWithVisibility;
    };

    const processBuyersTable = (
        buyers: BuyerWithVisibility[],
        tableFilter: TableFilter
    ) => {
        let processedBuyers = [...buyers];

        if (Object.keys(tableFilter.filter?.filters || {}).length > 0) {
            Object.keys(tableFilter.filter.filters).forEach((filterKey) => {
                const filter = (
                    tableFilter.filter.filters as unknown as {
                        [key: string]: {
                            filterVal: any;
                            comparator: any;
                        };
                    }
                )[filterKey];

                const filterValue = filter.filterVal.toLowerCase();

                processedBuyers = processedBuyers.filter((b) => {
                    if (filterKey === 'corporateAffiliationName') {
                        if (filter.comparator == 'eq') {
                            return b.corporateAffiliationName != ''
                                ? b.corporateAffiliationName
                                      .toLowerCase()
                                      .trim() === filterValue
                                : b.userFullName.toLowerCase().trim() ===
                                      filterValue;
                        }

                        return b.corporateAffiliationName != ''
                            ? b.corporateAffiliationName
                                  .toLowerCase()
                                  .includes(filterValue)
                            : b.userFullName
                                  .toLowerCase()
                                  .includes(filterValue);
                    }

                    if (filter.comparator == 'eq') {
                        return (
                            (b as any)[filterKey]
                                .toString()
                                .toLowerCase()
                                .trim() === filterValue
                        );
                    } else {
                        return (b as any)[filterKey]
                            .toString()
                            .toLowerCase()
                            .includes(filterValue);
                    }
                });
            });
        } else processedBuyers = mapBuyers();

        processedBuyers = processedBuyers.sort((a, b) => {
            const fieldA =
                a.corporateAffiliationName !== ''
                    ? a.corporateAffiliationName
                    : a.userFullName;
            const fieldB =
                b.corporateAffiliationName !== ''
                    ? b.corporateAffiliationName
                    : b.userFullName;
            const dir = tableFilter.sort?.dir || 'asc';

            if (fieldA < fieldB) {
                return dir === 'asc' ? -1 : 1;
            }
            if (fieldA > fieldB) {
                return dir === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return processedBuyers;
    };

    const handleTableChange = (_type: any, newState: any) => {
        const { page, sizePerPage, sortField, sortOrder, filters } = newState;

        const updatedPostObj = new TableFilter(
            { filters, logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: sortField }
        );
        setCurrentSort(updatedPostObj);

        const processedBuyers = processBuyersTable(buyers, updatedPostObj);
        setBuyers(processedBuyers);
    };

    useEffect(() => {
        const buyersWithVisibility = mapBuyers();
        const processedBuyers = processBuyersTable(
            buyersWithVisibility,
            currentSort
        );

        setBuyers((prevBuyers) => {
            const newBuyers = processedBuyers.map((buyer) => ({
                ...buyer,
                isSelectedForTeaser:
                    prevBuyers.find((b) => b.id === buyer.id)
                        ?.isSelectedForTeaser || false,
            }));
            return newBuyers;
        });
    }, [props.buyers, props.buyersVisibilityStates, currentSort]);

    useEffect(() => {
        setBuyers((prevBuyers) => {
            return prevBuyers.map((buyer) => ({
                ...buyer,
                isSelectedForTeaser: buyerIdsToPostTeaser.includes(buyer.id),
            }));
        });
    }, [buyerIdsToPostTeaser]);

    const closeBuyerProfile = () => {
        setIsBuyerProfilePopupVisible(false);
        setSelectedBuyerId(0);
    };

    const toggleBuyerVisibility = () => {
        setIsBuyerVisibilityLoading(true);

        const isCurrentlyVisible = buyers.find(
            (b) => b.id === selectedBuyerIdToToggleVisibility
        )?.isBuyerVisible;

        axios
            .put(
                `/api/Marketplace/Engagements/SFAS/${props.engagementId}/BuyerVisibility/`,
                {
                    buyerId: selectedBuyerIdToToggleVisibility,
                    isVisible: !isCurrentlyVisible,
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    if (isCurrentlyVisible) {
                        setBuyerIdsToPostTeaser((prev) => {
                            const updatedIds = prev.filter(
                                (id) => id !== selectedBuyerIdToToggleVisibility
                            );
                            return updatedIds;
                        });
                    }

                    setIsVisibilityConfirmationPopupVisible(false);
                    setSelectedBuyerIdToToggleVisibility(0);

                    props.refreshBuyersData();
                }
            })
            .catch((error) => {
                createNotification(error, 'error');
            })
            .finally(() => {
                setIsBuyerVisibilityLoading(false);
            });
    };

    const postTeaserToBuyers = () => {
        setIsTeaserPostingLoading(true);

        axios
            .post(
                `/api/Marketplace/Engagements/SFAS/${props.engagementId}/PostTeaserToBuyers`,
                {
                    buyerIds: buyerIdsToPostTeaser,
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    setIsTeaserPostConfirmationPopupVisible(false);
                    setBuyerIdsToPostTeaser([]);

                    props.refreshBuyersData();
                } else {
                    createNotification(
                        'An error occurred while posting teasers to selected buyers.',
                        'error'
                    );
                }
            })
            .catch((error) => {
                createNotification(error, 'error');
            })
            .finally(() => {
                setIsTeaserPostingLoading(false);
            });
    };

    const onBuyerSelectedForTeaserPost = (
        buyerId: number,
        isSelected: boolean
    ) => {
        setBuyerIdsToPostTeaser((prevIds) => {
            if (isSelected) {
                return prevIds.includes(buyerId)
                    ? prevIds
                    : [...prevIds, buyerId];
            } else {
                return prevIds.filter((id) => id !== buyerId);
            }
        });
    };

    const textFilterProps = textFilter({
        placeholder: 'Name',
    }) as TableColumnFilterProps;

    const renderSelectedPostTeaserBuyersList = () => {
        return (
            <div>
                <p>
                    Are you sure you want to post a teaser to the following
                    buyers?
                </p>
                <ul>
                    {buyers
                        .filter((buyer) =>
                            buyerIdsToPostTeaser.includes(buyer.id)
                        )
                        .map((buyer) => (
                            <li key={buyer.id} className="mb-1">
                                {!buyer.corporateAffiliationName ||
                                buyer.corporateAffiliationName === ''
                                    ? buyer.userFullName
                                    : buyer.corporateAffiliationName}
                            </li>
                        ))}
                </ul>
            </div>
        );
    };

    const getColumns = () => [
        {
            dataField: 'id',
            text: 'Id',
            hidden: true,
        },
        {
            dataField: 'corporateAffiliationName',
            text: 'Buyer',
            sort: true,
            filter: textFilterProps,
            headerClasses: 'buyer-column-header',
            formatter: (cell: any, row: BuyerWithVisibility) => {
                const companyName =
                    row.profile?.companyName ||
                    cell ||
                    row.userFullName ||
                    'NA';

                return (
                    <ClickableText
                        row={row}
                        readonly={false}
                        value={companyName}
                        action={(data: string, row: BuyerWithVisibility) => {
                            setSelectedBuyerId(row.id);
                            setIsBuyerProfilePopupVisible(true);
                        }}
                    />
                );
            },
        },
        {
            dataField: 'isBuyerVisible',
            text: 'Visiblity to Seller',
            formatter: (cell: boolean, row: BuyerWithVisibility) => (
                <input
                    key={row.id}
                    type="checkbox"
                    checked={!!cell}
                    disabled={
                        props.isLocked ||
                        row.isTeaserPosted ||
                        isBuyerVisibilityLoading ||
                        isTeaserPostingLoading
                    }
                    onChange={() => {
                        setSelectedBuyerIdToToggleVisibility(row.id);
                        setIsVisibilityConfirmationPopupVisible(true);
                    }}
                />
            ),
        },
        {
            dataField: 'isTeaserPosted',
            text: 'Teaser Posted',
            formatter: (cell: boolean, row: BuyerWithVisibility) => (
                <>
                    <input
                        key={row.id}
                        type="checkbox"
                        checked={!!cell || row.isSelectedForTeaser}
                        disabled={
                            props.isLocked ||
                            !row.isBuyerVisible ||
                            isBuyerVisibilityLoading ||
                            isTeaserPostingLoading ||
                            !!cell ||
                            !props.isTeaserPostingAvailable ||
                            props.wasTeaserPostingCompleted
                        }
                        onChange={(event) =>
                            onBuyerSelectedForTeaserPost(
                                row.id,
                                event.target.checked
                            )
                        }
                    />
                    {!props.isTeaserPostingAvailable && !props.wasTeaserPostingCompleted ? (
                        <InfoTooltip
                            idText={`teaser-post-info-tooltip-${row.id}`}
                            text="Teaser Posting is not available until the corresponding event is active. </br>Moreover, buyer will not see the opportunty until teaser is confirmed."
                            place="top"
                        />
                    ) : (
                        <></>
                    )}
                </>
            ),
        },
    ];

    return (
        <>
            <div className="engagement-buyers-visibility-table">
                <div className="button-container">
                    <Button
                        type="button"
                        className="btn btn-primary"
                        disabled={
                            buyerIdsToPostTeaser.length === 0 ||
                            !props.isTeaserPostingAvailable
                        }
                        onClick={() => {
                            setIsTeaserPostConfirmationPopupVisible(true);
                        }}>
                        Post Teaser to Selected Buyers
                    </Button>
                </div>

                <BootstrapTable
                    striped
                    bordered
                    bootstrap4
                    remote
                    keyField="id"
                    data={buyers}
                    columns={getColumns()}
                    defaultSorted={defaultSorted}
                    filter={filterFactory()}
                    pagination={paginationFactory({
                        page,
                        sizePerPage,
                        totalSize,
                        tooltipTargetId: 'current-events-tooltip',
                    })}
                    onTableChange={handleTableChange}
                    noDataIndication="No Buyers available"
                />

                {isBuyerProfilePopupVisible && !!selectedBuyerId && (
                    <BuyerProfilePreviewPopup
                        buyerId={selectedBuyerId}
                        close={closeBuyerProfile}
                    />
                )}

                {isVisibilityConfirmationPopupVisible && (
                    <ConfirmationModal
                        isVisible={isVisibilityConfirmationPopupVisible}
                        setIsVisible={setIsVisibilityConfirmationPopupVisible}
                        header={'Buyer Visibility Toggle'}
                        confirmationText={`Are you sure you want to toggle the visibility of this buyer?`}
                        onConfirm={() => toggleBuyerVisibility()}
                        isLoading={isBuyerVisibilityLoading}
                        params={''}
                        nextButtonText="Confirm"
                        cancelButtonText="Cancel & Close"
                    />
                )}

                {isTeaserPostConfirmationPopupVisible && (
                    <ConfirmationModal
                        isVisible={isTeaserPostConfirmationPopupVisible}
                        setIsVisible={setIsTeaserPostConfirmationPopupVisible}
                        header={'Teaser Post Confirmation'}
                        confirmationText={
                            buyerIdsToPostTeaser.length > 0
                                ? renderSelectedPostTeaserBuyersList()
                                : 'Please select buyers to post the teaser to.'
                        }
                        onConfirm={() =>
                            buyerIdsToPostTeaser.length > 0 &&
                            postTeaserToBuyers()
                        }
                        isLoading={isTeaserPostingLoading}
                        params={''}
                        nextButtonText="Confirm"
                        cancelButtonText="Cancel & Close"
                    />
                )}
            </div>
        </>
    );
};

export default EngagementBuyersVisibilityTable;
